/**
 * @render react
 * @name Footer
 * @example
 * <Footer />
 * Example when mobile sticky ad should not be rendered on the page:
 * <Footer mobileStickyAd={false} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import {
  HtmlSafe,
  withConfig,
  useDidomiPreferencesMenuItem,
} from 'polaris-coreweb/exports';


import { withPolaris } from '@autovia-uk/polaris-components/components/protons/Polaris';

import { Ad } from '@autovia-uk/polaris-components/components/atoms/Ad';
import { Navigation } from '@autovia-uk/polaris-components/components/molecules/Navigation'; // Button, NavigationItem (Icon and Link)
import { Social } from '@autovia-uk/polaris-components/components/molecules/Social'; // Icon, Link
import BannerEvent from 'Organisms/BannerEvent/BannerEvent'; // Heading, Popup
import { useLayout } from 'Helpers/hooks/useLayout';

import './_style.scss';
// HELPERS
// In CMS new lines added via special symbols \r\n
// swap them for line break tag
// simple swap '\r\n' -> '<br />' in string doesn't give wanted solution, so making react components is a must
// https://medium.com/@kevinsimper/react-newline-to-break-nl2br-a1c240ba746
const renderCMSText = (str) => {
  if (typeof str !== 'string') return '';

  const newLine = '\r\n';
  if (!str.includes(newLine)) return str;

  return (
    <>
      {
        str.split(newLine).map((line, lineIndex) => (
          <React.Fragment key={`line_${lineIndex}`}>
            {line}
            <br />
          </React.Fragment>
        ))
      }
    </>
  );
};

/**
 * Footer component
 *
 * @param {string} props.context.footer.legalDisclaimer - Legal disclaimer text.
 * @param {Object} props.context.footer.navigation - The navigation configuration object.
 * @param {Object} props.context.social - Social media configuration.
 * @param {string} props.context.globalSettings.copyright - Copyright text.
 * @param {boolean} props.isDesktop - Whether the current viewport is desktop (provided by withViewport HOC).
 * @param {string} props.layoutType - The layout type.
 * @param {boolean} props.mobileStickyAd - Whether to show mobile sticky ad (hidden on desktop automatically).
 */
const Footer = (props) => {
  const {
    context = {},
    isDesktop,
    layoutData,
    layoutType,
    mobileStickyAd,
  } = props;

  const {
    cssPrefix,
    didomi,
    footer = {},
    globalSettings = {},
    social,
  } = context;

  const {
    legalDisclaimer,
    navigation,
  } = footer;

  const {
    copyright,
  } = globalSettings;

  const layout = layoutData?.page?.fullWidth ? 'fullWidth' : layoutType;
  const {
    hideStickyFooterAd,
    useReducedFooter,
  } = useLayout(layout);

  // Hide sticky footer ad on desktop
  const isStickyFooterAdHidden = hideStickyFooterAd || isDesktop;

  const didomiPreferencesItem = useDidomiPreferencesMenuItem({
    enabled: Boolean(didomi?.publicApiKey && didomi?.noticeId),
  });

  // Build navigation arrays with privacy preference link if needed
  const footerNavigation = navigation?.footer ? [...navigation.footer] : [];
  const reducedFooterNavigation = navigation?.reducedfooter ? [...navigation.reducedfooter] : [];

  if (didomiPreferencesItem) {
    footerNavigation.push(didomiPreferencesItem);
  }

  if (useReducedFooter && reducedFooterNavigation && didomiPreferencesItem) {
    reducedFooterNavigation.push(didomiPreferencesItem);
  }

  return (
    <>
      <div className="screen-reader-text">
        <a href="#header">Skip to Header</a>
        <a href="#main">Skip to Content</a>
      </div>
      <footer
        className={classNames({
          [`${cssPrefix}__footer`]: true,
        })}
        id="footer"
      >
        <div className={`${cssPrefix}__footer--inner`}>
          <div className={`${cssPrefix}__footer--row`}>
            {useReducedFooter && reducedFooterNavigation.length > 0
              ? (
                <div className={`${cssPrefix}__footer--navigation`}>
                  <Navigation
                    content={reducedFooterNavigation}
                  />
                </div>
              )
              : (
                <div className={`${cssPrefix}__footer--navigation`}>
                  {footerNavigation.length > 0 && (
                    <Navigation
                      content={footerNavigation}
                    />
                  )}
                  <Social
                    platforms={social?.follow?.platforms || []}
                    extraClassNames={{ [`${cssPrefix}__footer--social`]: true }}
                  />
                </div>
              )
            }
          </div>

          <div className={`${cssPrefix}__footer--row`}>
            {legalDisclaimer && legalDisclaimer.length > 0 && (
              <div className={`${cssPrefix}__footer--legal-disclaimer`}>
                {legalDisclaimer.map((disclaimer, index) => (
                  <HtmlSafe key={index} html={disclaimer} />
                ))}
              </div>
            )}
            <div className={`${cssPrefix}__footer--copyright`}>
              <p>{renderCMSText(copyright)}</p>
            </div>
          </div>
        </div>

        {
          !isStickyFooterAdHidden
          && mobileStickyAd
          && (
            <Ad
              id="refresh-stickyfooter"
              type="stickyfooter"
              renderWhenViewable={false}
              targeting={{
                position: 'stickyfooter',
                placement: 'stickyfooter',
                refresh: 'yes',
              }}
              isMobileSticky
            />
          )
        }
      </footer>
      <BannerEvent cssPrefix={cssPrefix} />
    </>
  );
};

Footer.propTypes = {
  context: PropTypes.shape({
    footer: PropTypes.shape({
      legalDisclaimer: PropTypes.arrayOf(PropTypes.string),
      navigation: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape(),
        PropTypes.arrayOf(PropTypes.shape({
          url: PropTypes.string,
          title: PropTypes.string,
        })),
      ]),
    }),
    social: PropTypes.shape({
      follow: PropTypes.shape(),
    }),
    globalSettings: PropTypes.shape({
      copyright: PropTypes.string,
    }),
    didomi: PropTypes.shape({
      noticeId: PropTypes.string,
      publicApiKey: PropTypes.string,
      sdkPath: PropTypes.string,
    }),
    cssPrefix: PropTypes.string,
  }).isRequired,
  isDesktop: PropTypes.bool.isRequired,
  layoutData: PropTypes.shape(),
  layoutType: PropTypes.string,
  mobileStickyAd: PropTypes.bool,
};

Footer.defaultProps = {
  layoutData: {
    page: {
      fullWidth: false,
    },
  },
  layoutType: '',
  mobileStickyAd: true,
};

export default compose(withConfig, withPolaris)(Footer);
